import { useState, useEffect, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { register } from '../api/client'
import { trackEvent } from '../utils/analytics'
import GoogleSignInButton from '../components/shared/GoogleSignInButton'
import { safeReturnTo } from '../utils/returnTo'
import { useLocale } from '../i18n/context'

// Mirrors backend OAUTH_ENABLED (see docs/DECISIONS.md) — flag-disabled
// pending Google credentials + the S-7 server-side state fix.
const OAUTH_ENABLED = import.meta.env.VITE_OAUTH_ENABLED === 'true'

// Shared key with OnboardingPage — survives a page reload between register and onboarding.
const DEMO_LANG_KEY = 'cs-demo-lang'

type ErrorState = { message: string; isConflict?: boolean }

export default function RegisterPage() {
  const navigate = useNavigate()
  const { tr, locale } = useLocale()
  const t = tr.registerPage
  const [searchParams] = useSearchParams()
  const [displayName, setDisplayName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<ErrorState | null>(null)
  const [loading, setLoading] = useState(false)

  // Fire register_started once per browser session (back-navigation must not re-fire).
  useEffect(() => {
    const SESSION_KEY = 'cs-register-started'
    if (!sessionStorage.getItem(SESSION_KEY)) {
      sessionStorage.setItem(SESSION_KEY, '1')
      trackEvent('register_started')
    }
  }, [])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    if (password.length < 8) {
      setError({ message: t.passwordTooShort })
      return
    }
    setLoading(true)
    try {
      await register(email, password, displayName || undefined, locale)
      trackEvent('register_completed')
      // Carry the demo-detected language into onboarding via sessionStorage so
      // it survives a page reload between this redirect and /onboarding.
      const suggestedLang = searchParams.get('lang')
      if (suggestedLang && /^[a-z]{2,3}$/.test(suggestedLang)) {
        try { sessionStorage.setItem(DEMO_LANG_KEY, suggestedLang) } catch { /* ignore */ }
      }
      // A new account onboards first; a page that sent the learner to sign in (a shared
      // text's «Сохранить») is where onboarding then returns them.
      const returnTo = searchParams.get('returnTo')
      navigate(returnTo ? `/onboarding?returnTo=${encodeURIComponent(safeReturnTo(returnTo))}` : '/home', { replace: true })
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 409) {
        setError({ message: t.emailTaken, isConflict: true })
      } else {
        const data = (err as { response?: { data?: { detail?: unknown } } })?.response?.data
        const msg = typeof data?.detail === 'string' ? data.detail : t.failed
        setError({ message: msg })
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: 'var(--bg-page)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 16,
    }}>
      <div style={{
        width: '100%',
        maxWidth: 400,
        backgroundColor: 'var(--bg-surface)',
        borderRadius: 'var(--radius-md)',
        padding: 40,
        boxShadow: 'var(--shadow-float)',
        border: '1px solid var(--border)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <h1 style={{
            fontWeight: 700, fontSize: 24,
            color: 'var(--text-primary)', letterSpacing: '-0.02em',
            marginBottom: 6,
          }}>
            {t.title}
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
            {t.subtitle}
          </p>
        </div>

        {error && (
          <div style={{
            backgroundColor: 'var(--danger-soft)',
            border: '1px solid color-mix(in srgb, var(--danger) 30%, transparent)',
            borderRadius: 'var(--radius-sm)',
            padding: '10px 14px',
            color: 'var(--danger)',
            fontSize: 14,
            marginBottom: 20,
          }}>
            {error.message}{' '}
            {error.isConflict && (
              <Link to="/login" style={{ color: 'var(--danger)', fontWeight: 700, textDecoration: 'underline' }}>
                {t.signInInstead}
              </Link>
            )}
          </div>
        )}

        {/* OAuth — Google */}
        {OAUTH_ENABLED && (
          <>
            <div style={{ marginBottom: 16 }}>
              <GoogleSignInButton label={t.signUpWithGoogle} onError={(m) => setError({ message: m })} />
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                margin: '4px 0 16px',
                fontSize: 12,
                color: 'var(--text-secondary)',
              }}
              aria-hidden
            >
              <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
              {t.or}
              <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            </div>
          </>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={labelStyle}>{t.displayName}</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={t.namePlaceholder}
              maxLength={100}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>{t.email}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="you@example.com" // i18n-exempt: an address format, not copy
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>{t.password}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
              placeholder={t.passwordPlaceholder}
              minLength={8}
              style={inputStyle}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              ...primaryButtonStyle,
              opacity: loading ? 0.65 : 1,
              cursor: loading ? 'not-allowed' : 'pointer',
              marginTop: 4,
            }}
          >
            {loading ? t.submitting : t.submit}
          </button>
        </form>

        <p style={{ textAlign: 'center', marginTop: 24, fontSize: 14, color: 'var(--text-secondary)' }}>
          {t.haveAccount}{' '}
          <Link to="/login" style={{ color: 'var(--accent)', fontWeight: 600, textDecoration: 'none' }}>
            {t.signIn}
          </Link>
        </p>
      </div>
    </div>
  )
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--text-secondary)',
  marginBottom: 6,
}

const inputStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  backgroundColor: 'var(--bg-secondary)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  padding: '12px 16px',
  color: 'var(--text-primary)',
  fontSize: 15,
  outline: 'none',
  transition: 'border-color 0.15s',
  fontFamily: 'var(--font-ui)',
}

const primaryButtonStyle: React.CSSProperties = {
  backgroundColor: 'var(--accent-fill)',
  color: 'var(--text-on-accent)',
  border: 'none',
  borderRadius: 'var(--radius-pill)',
  padding: '13px 24px',
  fontSize: 15,
  fontWeight: 600,
  width: '100%',
  fontFamily: 'var(--font-ui)',
  transition: 'background-color 0.15s',
}
